import React from "react";
import { useCart } from "./CartContext";
import CartItems from "./CartItems";
import { toast } from "react-toastify";

const CartModal = ({ onClose, setStraper }) => {
  const { cart, dispatch } = useCart();

  const getInitialFormData = () => {
    if (
      Object.keys(localStorage).filter((item) => item.includes("attendees_"))
        .length > 0
    ) {
      return cart.reduce((acc, item) => {
        const savedFormData = JSON.parse(
          localStorage.getItem(`attendees_${item.event._id}`),
        );
        acc[item.event._id] = savedFormData || Array(item.seats).fill({});
        return acc;
      }, {});
    }
    return {};
  };

  const redirectToAttendeeInfo = async () => {
    const initialFormData = await getInitialFormData();

    if (Object.keys(initialFormData).length === 0 && cart.length === 0) {
      onClose();
      return;
    }

    if (Object.keys(initialFormData).length !== cart.length) {
      setStraper(2);
      return;
    }

    for (let item of cart) {
      if (item.seats !== initialFormData[item.id].length) {
        setStraper(2);
        return;
      }

      if (
        initialFormData[item.id].some((obj) => Object.keys(obj).length === 0)
      ) {
        setStraper(2);
        return;
      }

      const isVerified = localStorage.getItem("isAllEmailVerified");
      if (isVerified !== null && isVerified === "false") {
        setStraper(2);
        return;
      }
    }

    setStraper(3);
  };

  const handleRemoveFromCart = (id) => {
    dispatch({ type: "REMOVE_FROM_CART", payload: { id } });
  };

  const clearCart = () => {
    localStorage.clear();
    dispatch({ type: "CLEAR_CART" });
  };

  const increaseNoSeats = (id, seat) => {
    const cartItem = {
      id: id,
      seats: seat + 1,
    };
    dispatch({ type: "UPDATE_EVENT_SEATS_CART", payload: cartItem });
    toast.success("Class updated in cart!");
  };

  const decreaseNoSeats = async (id, seat) => {
    const initialFormData = await getInitialFormData();

    if (initialFormData[id]?.length >= seat) {
      initialFormData[id].pop();
      localStorage.setItem(
        `attendees_${id}`,
        JSON.stringify(initialFormData[id]),
      );
    }

    if (seat > 1) {
      const cartItem = {
        id: id,
        seats: seat - 1,
      };
      dispatch({ type: "UPDATE_EVENT_SEATS_CART", payload: cartItem });
      toast.success("Class updated in cart!");
    } else {
      localStorage.removeItem(`attendees_${id}`);
      handleRemoveFromCart(id);
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item?.price * item?.seats, 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.seats, 0);
  };

  return (
    <div className="w-full h-full max-w-7xl rounded-3xl flex flex-col shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#183864] to-[#B02526] px-6 lg:px-8 py-6 rounded-t-3xl">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-4">
            <h2 className="font-bold text-2xl lg:text-3xl leading-tight lg:leading-normal text-white">
              Classes Cart
            </h2>
            {cart.length > 0 && (
              <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold border border-white/30">
                {getTotalItems()} items
              </span>
            )}
          </div>
          <button
            className="bg-white/20 backdrop-blur-sm hover:bg-white/30 border border-white/30 w-full lg:w-auto px-6 h-[48px] lg:h-[53px] rounded-[16px] flex items-center justify-center text-white font-[700] text-[16px] lg:text-[18px] leading-[28.8px] cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            onClick={onClose}
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Close Cart
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="bg-white w-full flex-1 flex flex-col overflow-hidden">
        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 px-6">
            <div className="w-32 h-32 bg-gradient-to-br from-[#183864]/10 to-[#B02526]/10 rounded-full flex items-center justify-center mb-8 shadow-inner">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="w-16 h-16 fill-[#183864] opacity-60"
              >
                <path d="M7 18c-1.104 0-2 .896-2 2s.896 2 2 2 2-.896 2-2-.896-2-2-2zm-7-16v2h2l3.6 7.59-1.35 2.44c-.16.29-.25.63-.25.97 0 1.104.896 2 2 2h12v-2h-11.42c-.14 0-.25-.11-.25-.25l.03-.12 1.1-2h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.3.12-.48 0-.55-.45-1-1-1h-14.79l-.94-2h-3.27zm16 16c-1.104 0-2 .896-2 2s.896 2 2 2 2-.896 2-2-.896-2-2-2z" />
              </svg>
            </div>
            <div className="font-[700] text-[24px] lg:text-[28px] text-[#183864] mb-4">
              Your cart is empty
            </div>
            <div className="text-[#9A9EA6] text-[16px] lg:text-[18px] text-center max-w-md leading-relaxed">
              Browse our classes and add them to your cart to get started on
              your learning journey
            </div>
          </div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="hidden lg:flex justify-between mb-4 pb-4 border-b-2 border-gray-100 px-6 lg:px-8 pt-6 flex-shrink-0">
              <div className="font-[600] text-[16px] leading-[24px] text-[#183864] uppercase tracking-wide">
                Class & Quantity
              </div>
              <div className="font-[600] text-[16px] leading-[24px] text-[#183864] uppercase tracking-wide">
                Total
              </div>
            </div>

            {/* Scrollable Cart Items */}
            <div className="flex-1 overflow-y-auto px-6 lg:px-8 pb-6">
              <div className="space-y-6">
                {cart.map((item, index) => (
                  <CartItems
                    key={index}
                    item={item}
                    index={index}
                    handleRemoveFromCart={handleRemoveFromCart}
                    increaseNoSeat={increaseNoSeats}
                    decreaseNoSeats={decreaseNoSeats}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {cart.length > 0 && (
        <div className="bg-white border-t border-gray-200 px-6 lg:px-8 py-6 rounded-b-3xl">
          <div className="flex flex-col lg:flex-row gap-6 lg:justify-between lg:items-end">
            {/* Action Buttons */}
            <div className="order-2 lg:order-1 w-full lg:w-auto">
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <button
                  className="bg-gradient-to-r from-[#183864] to-[#1a4478] hover:from-[#1a4478] hover:to-[#183864] w-full sm:w-auto px-6 h-[48px] rounded-[16px] flex items-center justify-center text-white font-[600] text-[12px] cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                  onClick={redirectToAttendeeInfo}
                >
                  <svg
                    className="w-3 h-3 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  Return to Registrations
                </button>

                <button
                  className="bg-gradient-to-r from-[#B02526] to-[#8B1E1F] hover:from-[#8B1E1F] hover:to-[#B02526] w-full sm:w-auto px-6 h-[48px] rounded-[16px] flex items-center justify-center text-white font-[600] text-[12px] cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                  onClick={clearCart}
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Empty Cart
                </button>
              </div>
            </div>

            {/* Price Summary */}
            <div className="order-1 lg:order-2 w-full lg:w-[400px]">
              <div className="bg-white rounded-[20px] p-6 border border-gray-100 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-[18px] lg:text-[20px] font-[600] text-[#183864]">
                    Subtotal
                  </div>
                  <div className="font-[700] text-[24px] lg:text-[28px] leading-[32px] text-[#B02526]">
                    ${getTotalPrice().toFixed(2)} CAD
                  </div>
                </div>

                <div className="text-[#9A9EA6] text-[14px] lg:text-[16px] font-[500] mb-6 text-center">
                  Taxes calculated at checkout
                </div>

                {/* Proceed Button */}
                <button
                  className="bg-gradient-to-r from-[#B02526] to-[#8B1E1F] hover:from-[#8B1E1F] hover:to-[#B02526] w-full h-[48px] lg:h-[56px] rounded-[16px] flex items-center justify-center text-white font-[700] text-[16px] lg:text-[18px] leading-[28.8px] cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                  onClick={redirectToAttendeeInfo}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                  Proceed to Registration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartModal;
